/**
 * Факты объёма агроцикла для шапки страницы и карточек хаба (agroai-0e8,
 * замечания владельца 08.10): земля, хозяйства, поля, работы плана
 * «запущено K из M · идёт N · впереди», текущий этап. Без оценочных долей:
 * проценты и просрочка живут ниже, по этапам и работам, со своей основой.
 *
 * M и K — числа слоя метрик (`plan.works`, `plan.launched`); «идёт» — те из
 * запущенных, чьё окно накрывает дату среза (строки плана того же ответа);
 * «впереди» — работы плана без кампании (M − K). Шапке хватает одного ответа
 * `/agrocycles/{key}`: она не ждёт тяжёлой картины сезона.
 *
 * Здесь же — порядок работ виджета «Работы агроцикла и карта»: сверху идущие,
 * затем только что закрытые и вот-вот начинающиеся, ниже предстоящие, внизу
 * давно закрытые.
 */
import type { Work } from '../../data/types';
import type { AgrocycleRow, PlanStage } from '../agrocycle/agrocycleApi';
import { kpi, planAhead } from '../agrocycle/agrocycleView';

export interface PlanFacts {
  /** Работ плана (без отменённых); `null` — сервер не посчитал. */
  total: number | null;
  launched: number | null;
  /** Запущенные, окно которых накрывает дату среза. */
  active: number;
  /** Работы плана без кампании. */
  ahead: number | null;
}

const covers = (from: string | null | undefined, to: string | null | undefined, asOf: string) =>
  !!from && from <= asOf && (!to || to >= asOf);

export function planFacts(
  row: Pick<AgrocycleRow, 'kpis'> & { plan?: { stages: ReadonlyArray<PlanStage> } },
  asOf: string | null,
): PlanFacts {
  const total = kpi(row.kpis, 'plan.works');
  const launched = kpi(row.kpis, 'plan.launched');
  let active = 0;
  if (asOf && row.plan) {
    for (const stage of row.plan.stages) {
      for (const w of stage.works) {
        if (w.state === 'launched' && covers(w.window_from, w.window_to, asOf)) active += 1;
      }
    }
  }
  return {
    total,
    launched,
    active,
    ahead: planAhead(row.kpis),
  };
}

/* ------------------------------------------------------------- хаб */

export type HubSection = 'started' | 'not_started' | 'finished';

/** Разделы хаба: идут (свежие сверху), предстоящие (ближайшие сверху), закрытые (свежие сверху). */
export function hubSections<T extends Pick<AgrocycleRow, 'status' | 'plan_from' | 'plan_to' | 'key'>>(
  rows: ReadonlyArray<T>,
): Record<HubSection, T[]> {
  const out: Record<HubSection, T[]> = { started: [], not_started: [], finished: [] };
  for (const row of rows) out[row.status].push(row);
  const from = (r: T) => r.plan_from ?? '';
  const to = (r: T) => r.plan_to ?? '';
  out.started.sort((a, b) => from(b).localeCompare(from(a)) || a.key.localeCompare(b.key));
  out.not_started.sort((a, b) => from(a).localeCompare(from(b)) || a.key.localeCompare(b.key));
  out.finished.sort((a, b) => to(b).localeCompare(to(a)) || a.key.localeCompare(b.key));
  return out;
}

/** Где дата среза в окне цикла, 0..1; `null` — окна нет. */
export function seasonPosition(from: string | null | undefined, to: string | null | undefined, asOf: string | null): number | null {
  if (!from || !to || !asOf) return null;
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  const now = Date.parse(`${asOf}T00:00:00Z`);
  if (!(b > a)) return null;
  return Math.min(1, Math.max(0, (now - a) / (b - a)));
}

/* ------------------------------------------------- порядок работ */

export type WorkGroupKey = 'active' | 'justClosed' | 'startingSoon' | 'ahead' | 'closed';

export interface WorkGroup {
  key: WorkGroupKey;
  works: Work[];
}

/** «Только что закрыта» — окно закрылось не раньше стольких дней назад. */
export const JUST_CLOSED_DAYS = 14;
/** «Вот-вот начнётся» — окно откроется не позже стольких дней вперёд. */
export const STARTING_SOON_DAYS = 14;

const DAY = 86_400_000;
const shift = (iso: string, days: number) => new Date(Date.parse(`${iso}T00:00:00Z`) + days * DAY).toISOString().slice(0, 10);

/** Тяжесть закрытой или идущей работы: просрочка (га, иначе число работ), затем доля без отчёта. */
function severity(w: Work): [number, number] {
  const area = w.totals.area;
  const overdue = area?.overdue ?? (w.totals.overdue ?? 0);
  const total = area && area.total > 0 ? area.total : w.totals.obligations;
  const reported = area && area.total > 0 ? area.reported : w.totals.reported;
  const silent = total > 0 ? 1 - reported / total : 0;
  return [overdue ?? 0, silent];
}

function bySeverity(a: Work, b: Work): number {
  const [ao, as] = severity(a);
  const [bo, bs] = severity(b);
  return bo - ao || bs - as;
}

/**
 * Группы работ по дате среза. Внутри: идущие — по ближайшему концу окна, при
 * равенстве по тяжести; закрытые — по тяжести (просрочка, затем доля без
 * отчёта), при равенстве свежие сверху; предстоящие — по началу окна.
 * Пустые группы не возвращаются.
 */
export function groupWorks(works: ReadonlyArray<Work>, asOf: string | null): WorkGroup[] {
  const groups: Record<WorkGroupKey, Work[]> = { active: [], justClosed: [], startingSoon: [], ahead: [], closed: [] };
  const recent = asOf ? shift(asOf, -JUST_CLOSED_DAYS) : null;
  const soon = asOf ? shift(asOf, STARTING_SOON_DAYS) : null;
  for (const w of works) {
    if (w.status === 'active') groups.active.push(w);
    else if (w.status === 'done') (recent && w.window.to >= recent ? groups.justClosed : groups.closed).push(w);
    else (soon && w.window.from && w.window.from <= soon ? groups.startingSoon : groups.ahead).push(w);
  }
  groups.active.sort((a, b) => a.window.to.localeCompare(b.window.to) || bySeverity(a, b) || a.seq - b.seq);
  const closedOrder = (a: Work, b: Work) => bySeverity(a, b) || b.window.to.localeCompare(a.window.to) || a.seq - b.seq;
  groups.justClosed.sort(closedOrder);
  groups.closed.sort(closedOrder);
  const startOrder = (a: Work, b: Work) => (a.window.from || '9').localeCompare(b.window.from || '9') || a.seq - b.seq;
  groups.startingSoon.sort(startOrder);
  groups.ahead.sort(startOrder);
  const order: WorkGroupKey[] = ['active', 'justClosed', 'startingSoon', 'ahead', 'closed'];
  return order.filter((key) => groups[key].length > 0).map((key) => ({ key, works: groups[key] }));
}
